import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface StoryNavigationProps {
  onNext: () => void;
  onPrev: () => void;
  hasNext: boolean;
  hasPrev: boolean;
}

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-6 h-6 sm:w-8 sm:h-8"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 19.5L8.25 12l7.5-7.5"
    />
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-6 h-6 sm:w-8 sm:h-8"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
);

export const StoryNavigation = ({ onNext, onPrev, hasNext, hasPrev }: StoryNavigationProps) => {
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const swipeDistance = touchEndX.current - touchStartX.current;
    
    if (Math.abs(swipeDistance) >= minSwipeDistance) {
      if (swipeDistance > 0 && hasPrev) {
        onPrev();
      } else if (swipeDistance < 0 && hasNext) {
        onNext();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    const element = document.body;

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchmove', handleTouchMove);
    element.addEventListener('touchend', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [hasPrev, hasNext]);

  // Add touch areas for navigation on mobile
  const handleTouchArea = (direction: 'left' | 'right') => {
    if (direction === 'left' && hasPrev) {
      onPrev();
    } else if (direction === 'right' && hasNext) {
      onNext();
    }
  };

  return (
    <>
      {/* Touch areas for mobile - invisible but touchable */}
      <div className="sm:hidden fixed inset-0 z-10 pointer-events-none">
        <div 
          className="absolute left-0 top-0 w-1/3 h-full pointer-events-auto"
          onClick={() => handleTouchArea('left')}
        />
        <div 
          className="absolute right-0 top-0 w-1/3 h-full pointer-events-auto"
          onClick={() => handleTouchArea('right')}
        />
      </div>

      {/* Navigation arrows - only visible on desktop */}
      <div className="hidden sm:block">
        {/* Left Arrow */}
        {hasPrev && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={onPrev}
          >
            <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-1.5 sm:p-2 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all">
              <ChevronLeft />
            </div>
          </motion.div>
        )}

        {/* Right Arrow */}
        {hasNext && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={onNext}
          >
            <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-1.5 sm:p-2 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all">
              <ChevronRight />
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
};
